import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _metaCache = null;
let _metaPromise = null;

/**
 * useWeddingPermissions(weddingId)
 * Retorna { isOwner, role, permissions, loading, can(module, action), meta }
 * - permissions: { [module]: { [action]: bool } }
 * - meta: { modules, module_labels, action_labels, presets } (cached globalmente)
 * - can(module, action='view'): helper para checar uma permissão
 */
export function useWeddingPermissions(weddingId) {
  const [state, setState] = useState({
    isOwner: false,
    role: null,
    permissions: null,
    loading: true,
    error: null,
  });
  const [meta, setMeta] = useState(_metaCache);

  // Fetch global meta (uma vez)
  useEffect(() => {
    if (_metaCache) {
      setMeta(_metaCache);
      return;
    }
    if (!_metaPromise) {
      _metaPromise = axios
        .get(`${API}/me/permissions-meta`)
        .then((res) => {
          _metaCache = res.data;
          return res.data;
        })
        .catch(() => null);
    }
    _metaPromise.then((data) => {
      if (data) setMeta(data);
    });
  }, []);

  const fetchPerms = useCallback(async () => {
    if (!weddingId) {
      setState((s) => ({ ...s, loading: false, permissions: null }));
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await axios.get(`${API}/me/wedding-permissions/${weddingId}`);
      setState({
        isOwner: !!res.data.is_owner,
        role: res.data.role,
        permissions: res.data.permissions || {},
        loading: false,
        error: null,
      });
    } catch (err) {
      setState({
        isOwner: false,
        role: null,
        permissions: {},
        loading: false,
        error: err?.response?.data?.detail || "Sem acesso",
      });
    }
  }, [weddingId]);

  useEffect(() => {
    fetchPerms();
  }, [fetchPerms]);

  const can = useCallback(
    (module, action = "view") => {
      if (state.isOwner) return true;
      if (!state.permissions) return false;
      return !!state.permissions[module]?.[action];
    },
    [state.isOwner, state.permissions]
  );

  return { ...state, meta, can, refetch: fetchPerms };
}
